/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import dayjs from 'dayjs';

import { useFormik } from 'formik';
import { useGridApiRef } from '@mui/x-data-grid';
import { Box, Button, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import {
  listContracts,
  resetContractFilters,
  setContractFilters,
} from '../../modules/projectModule/contracts';
import ProjectList from '../../components/projectmng/ProjectList';
import CmpStatusChip from '../../components/common/datagrid/CmpStatusChip';
import { ProjectListBox } from '../../components/projectmng/ProjectListBox';
import ProjectStatusList from '../../components/projectmng/info/ProjectStatusList';
import ContractTypeList from '../../components/projectmng/info/ContractTypeList';
import { getNumberComma } from '../../utils/numberUtils';
import ChanceTotalSummary from '../../components/chancemng/ChanceTotalSummary';
import {
  CONTRACT_TYPE_MAINT,
  resolveContractDetailBasePath,
  writeStoredContractType,
} from '../../utils/contractType';

// "계약 관리" (2026-10-20 메뉴 통합, ksm_frontend feat/contract-menu 포팅) - 영업 > 프로젝트
// 관리 / 유지보수 관리를 유형 칩으로 합친 목록. 두 화면이 거의 같은 컴포넌트(ProjectList)·같은
// 백엔드 테이블을 썼어서, 백엔드의 기존 /campaign/list(= nml/mtm list와 같은 쿼리인데 prjTyp를
// 강제로 세팅하지 않는 엔드포인트, 스코프도 getForecastCmpSearchInf에서 이미 적용됨)를 그대로
// 재사용한다 - 유형 칩이 '전체'면 prjTyp를 비워서 보내고, 아니면 'N'/'M'을 보낸다. 상세/스텝
// 라우트는 그대로 두고(딥링크 호환), 행을 클릭하면 그 행의 유형에 맞는 기존 상세 화면
// (/projectmng 또는 /maintancetmng)으로 보낸다.
const ContractListContainer = ({ initialContractType }) => {
  const dispatch = useDispatch();
  let navigate = useNavigate();
  // 홈 위젯(신규 계약 확인)에서 ?noBill=Y 로 들어오면 그 필터로 초기 조회한다 - 카드=목록
  // 건수를 맞추기 위한 최소 딥링크 지원(메인화면 재검토 2, 2026-10-23 §6-10).
  const [searchParams] = useSearchParams();
  const { totals, contracts, defaultFilters, error, loading } = useSelector(
    ({ projectModule, loading }) => ({
      contracts: projectModule.contracts.contracts,
      totals: projectModule.contracts.totals,
      defaultFilters: projectModule.contracts.defaultFilters,
      error: projectModule.contracts.error,
      loading: loading['project/LIST_CONTRACTS'],
    }),
  );
  const [tableData, setTableData] = useState([]);

  const makeFilters = (filters) => {
    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : '';

    const searchData = {
      prjTyp: filters.searchPrjTyp,
      cmpGrade: filters.searchGrade,
      prjImpt: filters.searchPrjImpt,
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
      // 서버는 crtDptNm=부서명, crtNm=담당자명으로 LIKE 검색한다.
      crtDptNm: filters.searchDpt ? filters.searchDpt : '',
      crtNm: filters.searchUsr ? filters.searchUsr : '',
      // 홈 위젯 딥링크 필터(메인화면 재검토 2, 2026-10-23) - 비어 있으면 서버가 무시한다.
      noBill: filters.searchNoBill || undefined,
    };

    return searchData;
  };

  // 옛 /maintancetmng 라우트의 리다이렉트(App.js: /projectmng?type=maint)로 들어온 경우,
  // 쿼리로 지정된 유형을 한 번만 적용하고 기억해둔다. 쿼리가 없거나 못 알아보는 값이면
  // (initialContractType === null) 저장된/기본 유형을 그대로 쓴다.
  useEffect(() => {
    if (initialContractType === null || initialContractType === undefined) return;
    if (initialContractType === defaultFilters.searchPrjTyp) return;
    const newFilters = { ...defaultFilters, searchPrjTyp: initialContractType };
    dispatch(setContractFilters(newFilters));
    writeStoredContractType(window.localStorage, initialContractType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialContractType]);

  // 마운트 시 1회만 ?noBill= 딥링크를 읽어 defaultFilters에 반영하고, 언마운트 시 되돌린다
  // (redux에 남아 다음 진입에도 말없이 계속 걸리는 것을 막는다 - ChanceListContainer와 동일한
  // 패턴, 메인화면 재검토 2 §6-4).
  useEffect(() => {
    const noBill = searchParams.get('noBill');
    const deepLinkApplied = Boolean(noBill);
    if (deepLinkApplied) {
      dispatch(setContractFilters({ ...defaultFilters, searchNoBill: noBill }));
    }
    return () => {
      if (deepLinkApplied) {
        dispatch(resetContractFilters());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(listContracts(makeFilters(defaultFilters)));
  }, [defaultFilters]);

  useEffect(() => {
    if (contracts) {
      setTableData(contracts);
    }
  }, [contracts]);

  const handleFilters = (name, value) => {
    const newFilters = { ...defaultFilters, [name]: value };
    dispatch(setContractFilters(newFilters));
  };

  const handleTypeFilter = useCallback(
    (value) => {
      handleFilters('searchPrjTyp', value);
      writeStoredContractType(window.localStorage, value);
    },
    [defaultFilters],
  );

  const handleRowClick = useCallback((param, event) => {
    const basePath = resolveContractDetailBasePath(param.row.prjTyp);
    navigate(`${basePath}/${param.id}/${param.row.cmpGrade}/1`);
  }, []);

  const handleAdd = () => {
    navigate('/projectmng/add');
  };

  const apiRef = useGridApiRef();

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        type: 'string',
        editable: false,
        disableExport: true,
      },
      {
        field: 'prjTyp',
        headerName: '유형',
        minWidth: 110,
        flex: 0.4,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (!params || !params.row) return null;
          const isMaintenance = params.row.prjTyp === CONTRACT_TYPE_MAINT;
          return (
            <Chip
              label={isMaintenance ? '유지보수' : '프로젝트'}
              size="small"
              color={isMaintenance ? 'info' : 'default'}
              variant="outlined"
            />
          );
        },
      },
      {
        field: 'expDay',
        headerName: '매출월',
        minWidth: 100,
        flex: 0.5,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'vendorCmpNm',
        headerName: '매출처명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        editable: false,
      },
      {
        field: 'bookingDay',
        headerName: '계약일',
        minWidth: 100,
        flex: 0.5,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 200,
        flex: 1,
        description: '',
        align: 'left',
        headerAlign: 'left',
        editable: false,
      },
      {
        field: 'totalSales',
        headerName: '총매출액',
        minWidth: 150,
        flex: 1,
        description: '',
        align: 'center',
        headerAlign: 'center',
        editable: false,
        valueFormatter: (params) => getNumberComma(params),
        renderCell: (params) => getNumberComma(params.value),
      },
      {
        field: 'totalPurchase',
        headerName: '총매입액',
        minWidth: 150,
        flex: 1,
        description: '',
        align: 'center',
        headerAlign: 'center',
        editable: false,
        valueFormatter: (params) => getNumberComma(params),
        renderCell: (params) => getNumberComma(params.value),
      },
      {
        field: 'totalProfit',
        headerName: '매출이익',
        minWidth: 150,
        flex: 1,
        description: '',
        align: 'center',
        headerAlign: 'center',
        editable: false,
        valueFormatter: (params) => getNumberComma(params),
        renderCell: (params) => getNumberComma(params.value),
      },

      {
        field: 'cmpStatus',
        headerName: '상태',
        minWidth: 150,
        flex: 0.8,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: CmpStatusChip,
      },
      {
        field: 'crtDptNm',
        headerName: '부서',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
      },
      {
        field: 'crtNm',
        headerName: '영업담당자',
        minWidth: 100,
        flex: 0.5,
        type: 'string',
        editable: false,
        valueGetter: (_, row) => {
          if (!row) return '';
          return `${row.crtNm || ''} ${row.crtGradeNm || ''}`;
        },
        renderCell: (params) => {
          if (!params.row) return null;
          return (
            <div>
              {params.row.crtNm || ''} {params.row.crtGradeNm || ''}
            </div>
          );
        },
      },
      {
        field: 'prjNo',
        headerName: '프로젝트 번호',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
    ],
    [],
  );

  const initialValues = {
    searchPrjImpt: defaultFilters.searchPrjImpt,
    searchKeyword: defaultFilters.searchKeyword,
    searchWords: defaultFilters.searchWords,
    searchDpt: defaultFilters.searchDpt ?? '',
    searchUsr: defaultFilters.searchUsr ?? '',
    searchStrtDt: defaultFilters.searchStrtDt,
    searchEndDt: defaultFilters.searchEndDt,
  };

  const validationSchema = yup.object({
    searchPrjImpt: yup.string().nullable(),
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('검색 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const searchData = {
        searchGrade: defaultFilters.searchGrade,
        searchPrjTyp: defaultFilters.searchPrjTyp,
        searchPrjImpt: values.searchPrjImpt,
        searchKeyword: values.searchKeyword,
        searchWords: values.searchWords,
        searchStrtDt: values.searchStrtDt,
        searchEndDt: values.searchEndDt,
      };
      await dispatch(setContractFilters(searchData));
      setSubmitting(false);
    },
  });
  const handleReset = () => {
    dispatch(resetContractFilters());
  };

  return (
    <>
      <BreadcrumbsContainer
        title="수주 관리/계약 관리/계약 리스트"
        icon="order"
        action={
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAdd}>
            신규 계약 등록
          </Button>
        }
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <ProjectListBox formik={formik} onReset={handleReset} />
        <ContractTypeList value={defaultFilters.searchPrjTyp} onChange={handleTypeFilter} />
        <ProjectStatusList filters={defaultFilters} onFilters={handleFilters} />
        <ChanceTotalSummary totals={totals} />
        <ProjectList
          apiRef={apiRef}
          columns={columns}
          rows={tableData}
          error={error}
          loading={loading}
          handleRowClick={handleRowClick}

          columnVisibilityModel={{
            // Hide columns status and traderName, the other columns will remain visible
            id: false,
          }}
        />
      </Box>
    </>
  );
};

export default ContractListContainer;
